import { Divider } from '@material-ui/core';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { observer } from 'mobx-react-lite';
import { withSnackbar, WithSnackbarProps } from 'notistack';
import * as qs from 'qs';
import React, { useContext, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import WindsorApi from '../../../api/WindsorApi';
import UserContext from '../../../auth/UserContext';
import LinkRouter from '../../../common/LinkRouter';
import LoadingIndicator from '../../../common/LoadingIndicator';
import { removeEmptyFields } from '../../../common/WorkflowUtils';
import MobxContext from '../../../mobx/MobxContext';
import WorkflowForm from './WorkflowForm';
import { buildEmptyWorkflow } from '../../../common/WorkflowUtils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      maxWidth: 1200,
      margin: 'auto'
    },
    divider: {
      marginBottom: 16,
      marginTop: 8
    }
  })
);

const windsorApi = new WindsorApi();

function WorkflowCreatePage(props: RouteComponentProps<any> & WithSnackbarProps) {
  const { match, history, enqueueSnackbar, location } = props;
  const familyId = match.params.familyId;
  const { cloneFrom, loadFromFile } = qs.parse(location.search, { ignoreQueryPrefix: true });

  const classes = useStyles({});
  const user = useContext(UserContext);
  const [workflow, setWorkflow] = useState(null);
  const [family, setFamily] = useState(null);
  const [tests, setTests] = useState(null);
  const { cspGroupStore, environmentStore } = useContext(MobxContext);

  useEffect(() => {
    if (cloneFrom) {
      const cloneFromJson = JSON.parse(cloneFrom);
      windsorApi.getWorkflow(cloneFromJson.familyId, cloneFromJson.workflowId).then(workflowToClone => {
        // Remove fields that will be changed in new workflow
        delete workflowToClone.Versions;
        delete workflowToClone.Archived;
        delete workflowToClone.GUID;
        delete workflowToClone.LastExecutionTime;
        delete workflowToClone.LastExecutionGUID;
        delete workflowToClone.WorkflowStatus;
        delete workflowToClone.LastModified;
        delete workflowToClone.NotificationTopicARN;
        // Set new family
        workflowToClone.Family = familyId;
        // Set new owner
        workflowToClone.WorkflowAccess.WorkflowOwner = user.email;
        // Clear name
        workflowToClone.Name = '';
        setWorkflow(workflowToClone);
      });
    } else {
      setWorkflow(buildEmptyWorkflow(familyId, user.email));
    }
  }, [cloneFrom, familyId, user.email]);

  useEffect(() => {
    Promise.all([
      windsorApi.getFamily(familyId),
      windsorApi.getTests(familyId)
    ]).then(([family, tests]) => {
      setFamily(family);
      setTests(tests);
    });
  }, [familyId]);

  const onWorkflowChange = (workflow: any) => {
    setWorkflow(workflow);
  };

  const onSave = () => {
    const workflowToSave = { ...workflow, Family: familyId };
    // Remove empty fields
    removeEmptyFields(workflowToSave);
    return windsorApi.createWorkflow(workflowToSave);
  };

  const onSaveComplete = (workflow: any) => {
    enqueueSnackbar('Workflow created');
    history.push(`/families/${familyId}/workflows/${workflow.GUID}`);
  };

  const onCancel = () => {
    history.push(`/families/${familyId}/workflows`);
  };

  if (workflow && family && tests && cspGroupStore.groups && environmentStore.environments) {
    return (
      <div className={classes.root}>
        <Breadcrumbs aria-label="breadcrumb">
          <LinkRouter color="textSecondary" to={`/families`}>
            Families
          </LinkRouter>
          <LinkRouter color="textSecondary" to={`/families/${family.GUID}`}>
            {family.Name}
          </LinkRouter>
          <LinkRouter color="textSecondary" to={`/families/${family.GUID}`}>
            Workflows
          </LinkRouter>
          <Typography color="textPrimary">New</Typography>
        </Breadcrumbs>
        <Divider className={classes.divider} />
        <WorkflowForm
          title="Create Workflow"
          showFileUpload={loadFromFile === 'true'}
          workflow={workflow}
          family={family}
          cspGroups={cspGroupStore.groups}
          tests={tests}
          environments={environmentStore.environments}
          onWorkflowChange={onWorkflowChange}
          onSave={onSave}
          onSaveComplete={onSaveComplete}
          onCancel={onCancel}
        />
      </div >
    );
  } else {
    return <LoadingIndicator loading={true} />;
  }
};

export default withRouter(withSnackbar(observer(WorkflowCreatePage)));
